// Password hashing using Web Crypto API
const SESSION_MAX_AGE_SECONDS = 30 * 24 * 60 * 60;

async function hashPassword(password: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(password);
  const hash = await crypto.subtle.digest("SHA-256", data);
  return Array.from(new Uint8Array(hash))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

async function verifyPassword(password: string, hashedPassword: string): Promise<boolean> {
  const hashed = await hashPassword(password);
  return hashed === hashedPassword;
}

function normalizeEmail(email: string): string {
  return email.trim().toLowerCase();
}

function isValidEmail(email: string): boolean {
  return /^\S+@\S+\.\S+$/.test(email.trim());
}

function normalizePhoneNumber(phone: string): string {
  const digits = phone.replace(/\D/g, "");
  return digits;
}

function getPhoneLookupValues(phone: string): string[] {
  const digits = phone.replace(/\D/g, "");
  return [digits];
}

function isValidPhoneNumber(phone: string): boolean {
  const digits = phone.replace(/\D/g, "");
  return digits.length === 10 && digits.startsWith("0");
}

function makePhoneSignupEmail(phone: string): string {
  return `phone-${phone.replace(/\D/g, "")}@patatasks.local`;
}

function normalizeStoredEmail(email: string | null | undefined): string | null {
  if (!email) return null;
  if (email.startsWith("phone-") && email.endsWith("@patatasks.local")) {
    return null;
  }
  return email;
}

function buildSessionCookie(
  sessionId: string,
  requestUrl: string,
  maxAgeSeconds = SESSION_MAX_AGE_SECONDS,
): string {
  const isHttps = new URL(requestUrl).protocol === "https:";

  // For local development with HTTP, still set cookies properly
  // Cloudflare Pages dev server handles this correctly
  const sameSite = isHttps ? "SameSite=None; Secure" : "SameSite=Lax";

  return `session=${sessionId}; Path=/; HttpOnly; ${sameSite}; Max-Age=${maxAgeSeconds}; Priority=High`;
}

function parseSessionCookie(cookieHeader: string | null): string | null {
  if (!cookieHeader) return null;

  for (const part of cookieHeader.split(";")) {
    const [name, ...valueParts] = part.trim().split("=");
    if (name === "session") {
      const value = valueParts.join("=");
      return value || null;
    }
  }

  return null;
}

async function getUsersPhoneColumn(db: D1Database): Promise<"phone" | "phone_number" | null> {
  try {
    const result = await db.prepare("PRAGMA table_info(users);").all<{ name: string }>();
    const columns = result.results?.map((row) => row.name) ?? [];

    if (columns.includes("phone_number")) return "phone_number";
    if (columns.includes("phone")) return "phone";
  } catch (e) {
    console.error("getUsersPhoneColumn error:", e);
  }

  // Default to phone_number to match the current schema when detection is not possible.
  return "phone_number";
}

async function createSession(db: D1Database, userId: string): Promise<string> {
  const sessionId = crypto.randomUUID();
  const expiresAt = new Date(Date.now() + SESSION_MAX_AGE_SECONDS * 1000).toISOString();

  await db
    .prepare("INSERT INTO sessions (id, userId, expiresAt) VALUES (?, ?, ?)")
    .bind(sessionId, userId, expiresAt)
    .run();

  return sessionId;
}

async function deleteSession(db: D1Database, sessionId: string): Promise<void> {
  await db.prepare("DELETE FROM sessions WHERE id = ?").bind(sessionId).run();
}

async function getUserFromSession(
  db: D1Database,
  sessionId: string,
): Promise<{
  id: string;
  fullName: string;
  email: string;
  phone: string | null;
  balance: number;
  createdAt: string;
  accountLocked: number;
  lockThreshold: number;
  maintenancePaid: number;
  onboardingComplete: number;
  isAdmin: number;
} | null> {
  try {
    const phoneColumn = await getUsersPhoneColumn(db);
    const session = await db
      .prepare("SELECT * FROM sessions WHERE id = ? AND expiresAt > ?")
      .bind(sessionId, new Date().toISOString())
      .first();

    if (!session) return null;

    const user = await db
      .prepare(
        `SELECT id, fullName, email, ${phoneColumn ? `${phoneColumn} AS phone` : "NULL AS phone"}, balance, createdAt, account_locked AS accountLocked, lock_threshold AS lockThreshold, maintenance_paid AS maintenancePaid, onboarding_complete AS onboardingComplete, is_admin AS isAdmin FROM users WHERE id = ?`,
      )
      .bind(session.userId)
      .first();

    if (!user) return null;

    return {
      ...(user as any),
      email: normalizeStoredEmail((user as any).email),
    };
  } catch (error) {
    console.error("getUserFromSession error:", error);
    return null;
  }
}

function isLegacyHash(hashedPassword: string): boolean {
  // Current system uses SHA-256 hex strings (64 chars).
  // Legacy hashes from older systems were typically shorter or used different formats.
  // Since this is a new deployment, no legacy hashes exist yet.
  return false;
}

export {
  hashPassword,
  verifyPassword,
  normalizeEmail,
  normalizePhoneNumber,
  getPhoneLookupValues,
  isValidEmail,
  isValidPhoneNumber,
  makePhoneSignupEmail,
  normalizeStoredEmail,
  buildSessionCookie,
  parseSessionCookie,
  createSession,
  deleteSession,
  getUserFromSession,
  isLegacyHash,
};
